import type { ProjectSummary } from "@/lib/api";

export type BadgeVariant = "default" | "secondary" | "destructive" | "outline" | "warning";

export interface EffectiveStatus {
  label: string;
  variant: BadgeVariant;
}

const STATUS_LABELS: Record<string, EffectiveStatus> = {
  pending: { label: "Pending", variant: "secondary" },
  creating_repo: { label: "Creating repo", variant: "outline" },
  scaffolding: { label: "Scaffolding", variant: "outline" },
  preparing: { label: "Preparing", variant: "outline" },
  personalizing: { label: "Personalizing", variant: "outline" },
  setting_up_supabase: { label: "Setting up Supabase", variant: "outline" },
  pushing: { label: "Pushing", variant: "outline" },
  setting_up_railway: { label: "Setting up Railway", variant: "outline" },
  generating_instructions: { label: "Generating instructions", variant: "outline" },
  completed: { label: "Completed", variant: "default" },
  failed: { label: "Failed", variant: "destructive" },
  cancelled: { label: "Cancelled", variant: "secondary" },
};

/**
 * Derive the user-facing status label and badge variant for a project.
 *
 * An archived project reads "Archived" whatever its stored status says. When a
 * scaffold completed but the Railway deployment failed, this returns
 * "Partially completed" with a warning variant instead of a plain "Completed".
 */
export function getEffectiveStatus(project: ProjectSummary): EffectiveStatus {
  // Takes precedence over every other status, including the partially completed
  // one. An archived project's resources are gone on purpose, so showing its
  // owner "Completed" next to a dead Supabase link would read as data loss.
  if (project.shut_down) {
    return { label: "Archived", variant: "secondary" };
  }
  if (
    project.status === "completed" &&
    project.include_railway &&
    !!project.railway_error_message
  ) {
    return { label: "Partially completed", variant: "warning" };
  }
  return (
    STATUS_LABELS[project.status] ?? {
      label: project.status,
      variant: "secondary" as const,
    }
  );
}

/**
 * Name the external resources a project still links, in the order "Delete all"
 * tears them down (custom domain, Railway, Supabase, GitHub).
 *
 * Gated on the same fields the backend checks before it removes a project
 * record, so a confirmation built from this names exactly what "Delete all"
 * will attempt and what blocks removing the record.
 */
export function getLinkedResourceNames(project: ProjectSummary): string[] {
  const names: string[] = [];
  if (project.custom_subdomain) {
    names.push("Custom Domain");
  }
  if (project.railway_dashboard_url) {
    names.push("Railway");
  }
  if (project.supabase_dashboard_url) {
    names.push("Supabase");
  }
  if (project.github_url) {
    names.push("GitHub Repository");
  }
  return names;
}

/** Which of a project's provisioned resources are gone, and which are still up. */
export interface ResourceRemovalState {
  /** Resources the project provisioned that have since been removed. */
  removed: string[];
  /** Resources still standing, and therefore still costing money. */
  live: string[];
  /**
   * Resources that never provisioned, so their absence is not a removal.
   * Reported anyway, so a flagged row never silently omits a resource.
   */
  failed: string[];
}

/**
 * Detect a project whose teardown stopped halfway: some provisioned resources
 * are deleted while others are still up and billing.
 *
 * Deliberately kept out of `getEffectiveStatus`, which feeds the user-facing
 * project views. This is an admin concern, so only the admin project table
 * reads it.
 *
 * Returns null unless the project is genuinely half torn down.
 */
export function getResourceRemovalState(project: ProjectSummary): ResourceRemovalState | null {
  // A shut down (archived) project has its Supabase and Railway projects
  // deleted on purpose while its repo and its record here are kept, which is
  // exactly the shape this function flags. That is a finished state, not drift,
  // so it must not light up as half torn down.
  //
  // The Railway check is load-bearing, do not simplify this to a bare
  // `if (project.shut_down)`. The backend now stamps `shut_down` only once
  // every resource it attempted came back deleted, so a freshly archived
  // project should not reach this branch with a live Railway URL. Rows
  // archived under the earlier rule can: it stamped as soon as Supabase was
  // gone, even when the Railway delete had failed afterwards, leaving a
  // project still billing whose only warning was a dialog the admin has long
  // since closed. Those rows genuinely are half torn down and must keep
  // flagging, so this guard stays.
  if (project.shut_down && !project.railway_dashboard_url) {
    return null;
  }

  // Only completed projects can be judged. Before completion a missing URL
  // means "not provisioned yet", which is indistinguishable from "removed".
  if (project.status !== "completed") {
    return null;
  }

  const removed: string[] = [];
  const live: string[] = [];
  const failed: string[] = [];

  // Every completed project has a repo, so a missing URL is always a removal.
  (project.github_url ? live : removed).push("GitHub");

  if (project.supabase_dashboard_url) {
    live.push("Supabase");
  } else if (project.include_supabase) {
    removed.push("Supabase");
  }

  if (project.railway_dashboard_url) {
    live.push("Railway");
  } else if (project.include_railway && project.railway_error_message) {
    // A missing Railway URL alongside an error message never got provisioned at
    // all (that is "Partially completed"), so it is not evidence of a removal.
    failed.push("Railway");
  } else if (project.include_railway) {
    removed.push("Railway");
  }

  // A custom domain is optional, so its absence proves nothing. It can only
  // count towards what is still standing.
  if (project.custom_subdomain) {
    live.push("Custom domain");
  }

  // `failed` is informational and never triggers the flag on its own: a project
  // whose Railway setup failed and which was then torn down cleanly is not
  // half-removed.
  return removed.length > 0 && live.length > 0 ? { removed, live, failed } : null;
}
